import {
  getProcessDetailRoute,
  getTaskRoute
} from "@certa/common/utils/routes";
import type { ModelledAPIResponse } from "@certa/queries/types/globalSearch.types";
import { useNavigate } from "react-router";
import type { SearchItem } from "../GlobalSearchNext.schema";
import { transformSearchResult } from "../utils/searchTransformers";
import { useCallback } from "react";
import type { ListInteractionMode } from "@certa/catalyst/hooks/useListInteraction";
import { analyticsService, AnalyticsEvents } from "@certa/analytics";

type UseSearchNavigationProps = {
  setRecentSearches: (item: SearchItem) => void;
  onClose: () => void;
  keyboardCount: number;
};

export const useSearchNavigation = ({
  setRecentSearches,
  onClose,
  keyboardCount
}: UseSearchNavigationProps) => {
  const navigate = useNavigate();

  const onSelectSearchResult = useCallback(
    (
      result: ModelledAPIResponse.SearchResult,
      index: number,
      interactionMode: ListInteractionMode
    ) => {
      if (!result) return;
      const transformedResult = transformSearchResult(result);

      if (result.type === "workflow") {
        navigate(getProcessDetailRoute(result.id));
      } else if (result.type === "file" || result.type === "field") {
        navigate(
          getTaskRoute({
            processId: result.workflow.id,
            taskId: result.step.id
          })
        );
      }
      if (transformedResult) {
        setRecentSearches(transformedResult);
      }
      analyticsService.track(
        AnalyticsEvents.globalSearchEvents.GLOBAL_SEARCH_RESULT_CLICK,
        {
          result_type: result.type,
          interaction_mode: interactionMode,
          result_index: index + 1,
          certa_version: "OS2",
          keyboard_count: keyboardCount
        }
      );
      onClose();
    },
    [keyboardCount, onClose, navigate, setRecentSearches]
  );

  const onSelectRecentSearch = useCallback(
    (item: SearchItem, index: number, interactionMode: ListInteractionMode) => {
      if (item.type === "workflow") {
        navigate(getProcessDetailRoute(item.id));
      } else if (item.type === "file" || item.type === "field") {
        navigate(
          getTaskRoute({
            processId: item.workflowId,
            taskId: item.stepId
          })
        );
      }
      setRecentSearches(item);
      analyticsService.track(
        AnalyticsEvents.globalSearchEvents.GLOBAL_SEARCH_RECENT_SEARCH_CLICK,
        {
          result_type: item.type,
          interaction_mode: interactionMode,
          result_index: index + 1,
          certa_version: "OS2"
        }
      );
      onClose();
    },
    [navigate, setRecentSearches, onClose]
  );

  return { onSelectSearchResult, onSelectRecentSearch };
};
